import { normaliseProfile, slugify } from "./profiles/schema.js";
import { uniqueId } from "./profiles/index.js";

export const PROFILES_KEY = "mammoth-press:profiles";
export const SELECTED_KEY = "mammoth-press:selected";

export function getLocalStorage() {
  try {
    const storage = window.localStorage;
    const probe = "mammoth-press:probe";
    storage.setItem(probe, "1");
    storage.removeItem(probe);
    return storage;
  } catch {
    return null;
  }
}

export function createStore(storage) {
  function readRaw() {
    if (!storage) return [];
    try {
      const parsed = JSON.parse(storage.getItem(PROFILES_KEY) ?? "[]");
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  function read() {
    return readRaw()
      .map((item) => normaliseProfile(item).profile)
      .filter(Boolean)
      .map((profile) => ({ ...profile, builtIn: false }));
  }

  function write(list) {
    if (!storage) throw new Error("Saving isn't available in this browser (storage is blocked or you're in a private window). Download the profile instead.");
    try {
      storage.setItem(PROFILES_KEY, JSON.stringify(list));
    } catch {
      throw new Error("Couldn't save: browser storage is full or blocked. Download the profile instead.");
    }
  }

  // Entries we can't read (e.g. from a newer version) are kept as they are.
  const sameId = (item, id) => item && typeof item === "object" && item.id === id;

  return {
    available: Boolean(storage),
    listCustom: read,
    saveCustom(profile) {
      const raw = readRaw();
      const stored = { ...profile, builtIn: false };
      const index = raw.findIndex((item) => sameId(item, profile.id));
      if (index >= 0) raw[index] = stored;
      else raw.push(stored);
      write(raw);
      return read();
    },
    deleteCustom(id) {
      write(readRaw().filter((item) => !sameId(item, id)));
      return read();
    },
    getSelected() {
      try {
        return storage?.getItem(SELECTED_KEY) ?? null;
      } catch {
        return null;
      }
    },
    setSelected(id) {
      try {
        storage?.setItem(SELECTED_KEY, id);
      } catch {
        // Remembering the selection is a convenience; ignore failures.
      }
    },
  };
}

export function profileToJson(profile) {
  return `${JSON.stringify({ ...profile, builtIn: false }, null, 2)}\n`;
}

export function profileFilename(profile) {
  return `${slugify(profile.name)}.mammoth-profile.json`;
}

export function importProfile(text, takenIds) {
  let data;
  try {
    data = JSON.parse(text);
  } catch {
    return { profile: null, errors: ["That file isn't valid JSON."] };
  }
  const { profile, errors } = normaliseProfile(data);
  if (!profile) return { profile: null, errors };
  return { profile: { ...profile, builtIn: false, id: uniqueId(slugify(profile.name), takenIds) }, errors };
}
